'use client';

/**
 * ConsumersTable
 *
 * Renders the structured payload returned by `case_management_list_consumers`.
 * Issue #1432 Phase 2 — Wave 2 P1
 */

import Box from '@mui/material/Box';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { DataTableColumn } from '../../types/dataTable';
import { DataTable } from '../DataTable/DataTable';
import { GlassChip } from '../GlassChip';
import { RendererEmptyState } from './RendererEmptyState';
import { formatStatusLabel, getStatusColor } from './statusColor';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema
// ============================================================================

const ConsumerSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  status: z.string().optional(),
  lastActivity: z.string().optional(),
});

type ConsumerRow = z.infer<typeof ConsumerSchema>;

const ConsumersDataSchema = z.object({
  consumers: z.array(ConsumerSchema),
  total: z.number().optional(),
});

const ConsumersArraySchema = z.array(ConsumerSchema);

function parseConsumersData(data: unknown): ConsumerRow[] | null {
  const wrapped = ConsumersDataSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.consumers;

  const bareArray = ConsumersArraySchema.safeParse(data);
  if (bareArray.success) return bareArray.data;

  return null;
}

// ============================================================================
// Component
// ============================================================================

export const ConsumersTable = ({ data, onNavigate }: AstorToolRendererProps): ReactElement => {
  const { t, formatRelativeTime } = useI18n();
  const rows = useMemo(() => parseConsumersData(data), [data]);

  const columns: DataTableColumn<ConsumerRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('astorTools.consumers.name'),
        accessor: 'name',
      },
      {
        id: 'email',
        header: t('astorTools.consumers.email'),
        accessor: 'email',
      },
      {
        id: 'phone',
        header: t('astorTools.consumers.phone'),
        accessor: 'phone',
      },
      {
        id: 'status',
        header: t('astorTools.consumers.status'),
        accessor: 'status',
        sortable: true,
        cell: (value) => {
          const status = typeof value === 'string' ? value : '';
          if (!status) return '';
          return (
            <GlassChip
              label={formatStatusLabel(status)}
              color={getStatusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        id: 'lastActivity',
        header: t('astorTools.consumers.lastActivity'),
        accessor: 'lastActivity',
        sortable: true,
        cell: (value) => {
          if (typeof value !== 'string' || !value) return '';
          try {
            return formatRelativeTime(value);
          } catch {
            return value;
          }
        },
      },
    ],
    [t, formatRelativeTime],
  );

  if (rows === null) {
    return (
      <RendererEmptyState
        title={t('astorTools.error.parseFailed')}
        description={t('astorTools.error.parseFailedDescription')}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <RendererEmptyState
        title={t('astorTools.consumers.empty')}
        description={t('astorTools.consumers.emptyDescription')}
      />
    );
  }

  return (
    <Box sx={{ my: 1.5 }}>
      <DataTable<ConsumerRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        sortable
        paginated
        pagination={{ page: 0, pageSize: 10, pageSizeOptions: [10, 25, 50] }}
        density="compact"
        variant="glass"
        onRowClick={onNavigate ? (row) => onNavigate(`/consumers/${row.id}`) : undefined}
      />
    </Box>
  );
};

export default ConsumersTable;
